/**
 * Online-play constants — single source of truth for PROTOCOL.md v3,
 * MATCHMAKING.md §10 and ONLINE_RULES.md §9. Imported by client AND server;
 * never hard-code these values elsewhere.
 */

/** Wire protocol version. HELLO must advertise this exact value. */
export const PROTOCOL_VERSION = 3;

/** Online match sizes (MATCHMAKING.md §3 — 4-player stays local-only). */
export const ONLINE_PLAYER_COUNTS = [2, 3, 6] as const;
export type OnlinePlayerCount = (typeof ONLINE_PLAYER_COUNTS)[number];

/** Nickname constraints (PROTOCOL.md §3.1). */
export const NICKNAME_MAX_LEN = 12;

/** Maximum WebSocket text-frame size in bytes (PROTOCOL.md §1). */
export const MAX_MESSAGE_BYTES = 8 * 1024;

// ── Party (MATCHMAKING.md §5) ────────────────────────────
/** v2.1: 6 → 3 chars; 32³ = 32 768 combos with collision retry (MATCHMAKING §5.1). */
export const PARTY_CODE_LENGTH = 3;
/** Ambiguous glyphs (0/O, 1/I/L) excluded on purpose. */
export const PARTY_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** A party holds at most target-1 members → max is for the 6-player mode. */
export const PARTY_MAX_SIZE = 5;
/** Disconnected member's seat is held this long before auto-leave. */
export const PARTY_MEMBER_GRACE_MS = 60_000;

// ── Matchmaking queues (MATCHMAKING.md §6/§8) ────────────
export const QUEUE_TICK_MS = 1_000;
export const QUEUE_CAPACITY: Readonly<Record<OnlinePlayerCount, number>> = {
  2: 256,
  3: 256,
  6: 128,
};
/** A party waiting longer blocks later parties that would overflow the match. */
export const WAIT_PRIORITY_MS = 60_000;
/** Soft waiting hint shown to the user after this long in queue. */
export const SOFT_TIP_MS = 20_000;
/** "Try another mode" suggestion after this long in queue. */
export const SLOW_TIP_MS = 90_000;
/** QUEUE_STATUS push cadence (MATCHMAKING.md §6.4). */
export const QUEUE_STATUS_TICK_MS = 1_000;
/** Forced QUEUE_STATUS heartbeat even when nothing changed (§6.4). */
export const QUEUE_STATUS_HEARTBEAT_MS = 5_000;
/** Wait longer than this and the matchmaker may backfill with AI seats (§6.5). */
export const MATCH_FILL_AI_MS = 45_000;
/** Humanlike pause before an AI seat moves (§6.5). */
export const AI_MOVE_DELAY_MS = 800;

// ── Match lifecycle (ONLINE_RULES.md §7/§9) ──────────────
/** Countdown between MATCH_FOUND and GAME_START. */
export const MATCH_COUNTDOWN_MS = 3_000;
/** Rematch voting window after GAME_OVER. */
export const REMATCH_WINDOW_MS = 30_000;
/** Delay before an empty room is destroyed (reconnect slack). */
export const ROOM_CLEANUP_GRACE_MS = 5_000;

// ── Turn timers (ONLINE_RULES.md §3) ─────────────────────
export const TURN_TIMEOUT_MS = 30_000;
/** Client starts the "hurry up" warning when less than this remains. */
export const TURN_WARNING_MS = 10_000;
/** Consecutive autopilot moves before the seat is auto-resigned. */
export const CONSECUTIVE_AUTO_LIMIT = 3;

// ── Presence (ONLINE_RULES.md §4, PROTOCOL.md §7) ────────
/** Disconnected player's seat is held this long (autopilot covers turns). */
export const DISCONNECT_GRACE_MS = 60_000;
export const HEARTBEAT_PING_MS = 30_000;
export const HEARTBEAT_DEAD_MS = 60_000;

// ── Rate limits, token bucket (PROTOCOL.md §8) ───────────
export const RATE_LIMIT_MSG_PER_SEC = 10;
export const RATE_LIMIT_MOVE_PER_SEC = 2;
